import get from 'lodash/get';

import { resolvePayment } from '../payservice-helpers';
import Format from 'app/src/data/Format';
import CCTerminal from 'app/src/data/models/CCTerminal';
import GatewayConfig from 'app/src/data/models/GatewayConfig';
import PaymentModel from 'app/src/data/models/Payment';
import Localize from 'app/src/localize/Localize';
import Payment from 'app/src/payments/Payment';
import PaymentServiceTerminal from 'app/src/payments/terminals/PaymentServiceTerminal';
import { UnhandledStripeServiceError } from 'app/src/payments/terminals/StripeTerminalService';
import Terminal, { TerminalTransactionResponse } from 'app/src/payments/terminals/Terminal';
import { hasFeature } from 'root/api/admin/features';
import Modules from 'shared_modules/Modules';

const _DESCRIPTION = 'Lightspeed Payments Terminal Powered by Stripe';

class PollingTimeoutError extends Error {
  constructor(payment) {
    super();
    this._payment = payment;
  }

  get payment() {
    return this._payment;
  }
}

export class BaseStripeTerminal extends PaymentServiceTerminal {
  static _MINIMUM_POLLING_INTERVAL = 1000; // 1 second
  static _DEFAULT_MAX_POLLING = 50000; // 50 seconds
  static POLLING_TIME_BEFORE_FIRST_RESOLVE_ATTEMPT = 12000; // 12 seconds
  constructor(
    terminalModel,
    minimumPolling = BaseStripeTerminal._MINIMUM_POLLING_INTERVAL,
    maximumPolling = BaseStripeTerminal._DEFAULT_MAX_POLLING
  ) {
    super(terminalModel, minimumPolling);
    this._maxPollingTimeout = maximumPolling;
    this._paymentProcessingRolloutEnabled = false;
    this._shouldPoll = true;
    this.terminalModel = terminalModel;
    this.terminalResolvedPayment = false;
    this.stripeTerminalServiceInstance = null;
  }

  /**
   * Returns a string describing the terminal.
   *
   * @returns {string}    A description of the terminal
   */
  description() {
    return _DESCRIPTION;
  }

  /**
   * Re-initialize some of the class attributes
   * and disconnected the terminal
   */
  reset() {
    super.reset();
    this.terminalResolvedPayment = false;

    // We disconnect terminal if the terminal is connected.
    if (
      this.stripeTerminalServiceInstance &&
      this.stripeTerminalServiceInstance.getConnectionStatus() !== 'not_connected'
    ) {
      this._disconnectReader();
    }
  }

  /**
   * Specific types listed in the array support tipping.
   *
   * @returns {boolean}
   */
  supportsTipping() {
    return [
      CCTerminal.TerminalType.LIGHTSPEED_BBPOS_WISEPOSE_STRIPE,
      CCTerminal.TerminalType.LIGHTSPEED_STRIPE_READER_S700_STRIPE,
      CCTerminal.TerminalType.LIGHTSPEED_STRIPE_READER_S710_STRIPE,
    ].includes(this.model.get('type'));
  }

  /**
   * Communicates with the terminal to start a transaction.
   *
   * @param {Object} paymentOptions
   * @param {Decimal} paymentOptions.amount  The amount to transact with the terminal.
   * @param {Number} paymentOptions.saleID   The ID of the sale to which the transaction will be associated
   * @param {Number} paymentOptions.customerId  Optional - The customer ID associated with the sale
   * @param {GatewayConfig} paymentOptions.gatewayConfig  The gateway configuration used to transact the request
   * @param {Boolean} paymentOptions.isManualEntry  Optional - when true the terminal will be prompt for a manual
   *                                 entry (when available)
   * @param {Boolean} paymentOptions.isUnreferencedRefund Optional - when true the unreferenced refund flow will be
   *                                                      triggered
   * @param {String} paymentOptions.captureMethod Optional - The capture method ('automatic' or 'manual')
   */
  async processPayment(paymentOptions = {}) {
    const {
      amount,
      refundedPaymentID,
      isUnreferencedRefund,
      gatewayConfig,
      integrationType,
      saleTippingEnabled,
      cardToken,
      captureMethod,
      customerId,
    } = paymentOptions;
    let terminalTransactionResponse;
    let terminalPayment;
    let payment;

    this.gatewayConfig = paymentOptions.gatewayConfig;

    delete paymentOptions.saleTippingEnabled;
    // We reset the resolve Payment Flag every time we process or retry a payment
    this.terminalResolvedPayment = false;
    try {
      const stageParams = {
        ...paymentOptions,
        workflow:
          refundedPaymentID && amount.isNegative()
            ? PaymentModel.Workflow.TERMINAL_REFUNDS
            : PaymentModel.Workflow.TERMINAL_PAYMENTS,
        cardToken,
        integrationType,
        uniqueClientTxId: this.lspayMonitoring?._uniqueClientTxId ?? null,
      };
      this._paymentProcessingRolloutEnabled = await hasFeature('PaymentsProcessingRollout');
      const isTerminalTippingEnabled = await hasFeature('PaymentsTerminalTipping');
      if (isTerminalTippingEnabled) {
        stageParams.enableTerminalTipping =
          get(this.gatewayConfig, 'attributes.parameters.terminalTippingEnabled', false) &&
          isTerminalTippingEnabled &&
          saleTippingEnabled;
      }

      if (await hasFeature('PaymentsMonitoringStartTime')) {
        stageParams.startTime = this.lspayMonitoring?._start || Date.now();
        stageParams.clientTime = Date.now();
      }

      if (await hasFeature('PaymentsSurcharge')) {
        stageParams.enableSurcharge =
          parseFloat(get(this.gatewayConfig, 'attributes.parameters.surcharge', '0')) > 0;
      }

      if (Modules.hasModule('payments.pre_auth') && captureMethod) {
        stageParams.captureMethod = captureMethod;
      }

      // Preauthorization does not support tipping or surcharging — override both to false
      if (Modules.hasModule('payments.pre_auth') && captureMethod === 'manual') {
        stageParams.enableTerminalTipping = false;
        stageParams.enableSurcharge = false;
      }

      if (Modules.hasModule('payments.pre_auth') && customerId) {
        stageParams.customerId = customerId;
      }

      ({ payment, terminalPayment } = await this.stagePayment(stageParams));

      terminalPayment.serviceModel = payment;

      // you should get the payment integration type back on the payment which can then be toggle on in the polling
      await this.pollApiForPaymentStatus(payment);

      terminalPayment.model.set(
        'amount',
        Format.parseDecimal(payment.get('totalAmount'), terminalPayment.amount)
      );

      const paymentStatus = payment.get('status');
      const paymentTransaction = payment.paymentTransaction;

      if (paymentStatus === PaymentModel.Status.APPROVED) {
        terminalTransactionResponse = new TerminalTransactionResponse(
          Terminal.Response.APPROVED,
          paymentStatus,
          terminalPayment
        );

        return terminalTransactionResponse;
      }

      if (
        Modules.hasModule('payments.pre_auth') &&
        paymentStatus === PaymentModel.Status.PRE_AUTHORIZED
      ) {
        terminalTransactionResponse = new TerminalTransactionResponse(
          Terminal.Response.PRE_AUTHORIZED,
          paymentStatus,
          terminalPayment
        );

        return terminalTransactionResponse;
      }

      this.lspayMonitoring?.setAttributes({ terminalPayment });

      const paymentResponseMessage = paymentTransaction
        ? paymentTransaction.get('message') || ''
        : '';

      let response = Terminal.Response.SERVICE_EXCEPTION;
      switch (paymentStatus) {
        case PaymentModel.Status.DECLINED: // When a payment is declined
          response = Terminal.Response.DECLINED;
          break;

        case PaymentModel.Status.ERROR: // When a payment is in error
          response = Terminal.Response.ERROR;
          break;

        case PaymentModel.Status.CANCELLED: // When a payment transaction is cancelled
          if (
            paymentResponseMessage &&
            paymentResponseMessage.toLowerCase().includes('canceled by customer')
          ) {
            response = Terminal.Response.CANCELLED_FROM_TERMINAL;
          } else {
            response = Terminal.Response.CANCELLED_FROM_POS;
          }
          break;

        case PaymentModel.Status.UNKNOWN: // When a payment is having an UNKNOWN error
          response = Terminal.Response.UNKNOWN;
          break;
      }
      throw new TerminalTransactionResponse(
        response,
        paymentStatus,
        terminalPayment,
        paymentResponseMessage
      );
    } catch (err) {
      this.lspayMonitoring?.setAttributes({ processPaymentError: err });
      terminalTransactionResponse = this._handleServiceError(err, terminalPayment);
      throw terminalTransactionResponse;
    }
  }

  /**
   * Cancels payment at the Pay-Service level or Stripe Terminal level
   * Rules for Cancelling at Pay-service level:
   * we will try to cancel the payment at pay service level.
   * After we run keepPolling we will receive canceled action from payments, which will clear the state from stripe
   * @returns a Promise that resolves to an empty object once the command has been successfully canceled.
   * @return {error} If cancellation fails, the Promise resolves to an object with an error
   */
  async cancelPayment(payment) {
    try {
      // This will stop the polling
      this._shouldPoll = false;
      await super.cancelPayment(payment);
    } catch (err) {
      throw err;
    } finally {
      this._shouldPoll = true;
    }
  }

  /**
   * Polls the status of the specified payment at a regular interval.  Once the status is no longer
   * pending the promise will be resolved or rejected accordingly.
   *
   * @param {Payment} payment The payment on which the status is currently unknown
   * @param {number} elapsedTimeBeforeFirstResolveAttempt amount of polling time before resolving is attempted
   * @returns {*}
   */
  async pollApiForPaymentStatus(
    payment,
    elapsedTimeBeforeFirstResolveAttempt = POLLING_TIME_BEFORE_FIRST_RESOLVE_ATTEMPT
  ) {
    const timeBetweenResolveAttempts = 10000; // 10 seconds
    this._elapsedTime = 0;
    let lastResolveAttemptTime = 0;
    const featureFlag = await hasFeature('PaymentsFixPolling');
    for (
      ;
      this._elapsedTime < this._maxPollingTimeout;
      this._elapsedTime += this.nextPollingInterval(this._elapsedTime)
    ) {
      const paymentStatus = payment.get('status');

      if (
        featureFlag
          ? [
              PaymentModel.Status.APPROVED,
              PaymentModel.Status.DECLINED,
              PaymentModel.Status.CANCELLED,
              PaymentModel.Status.ERROR,
            ].includes(paymentStatus)
          : paymentStatus === PaymentModel.Status.CANCELLED
      ) {
        return payment;
      }
      // We use async within a for loop to control how often we poll from the backend

      // wait the set time
      // eslint-disable-next-line no-await-in-loop
      await new Promise((resolve) =>
        setTimeout(resolve, this.nextPollingInterval(this._elapsedTime))
      );

      let paymentResponse;
      let resolvedPayment;
      const amount = Format.parseMoney(get(payment.get('SalePayment'), 'amount'));
      if (
        elapsedTimeBeforeFirstResolveAttempt < this._elapsedTime &&
        this._elapsedTime - lastResolveAttemptTime > timeBetweenResolveAttempts &&
        amount.isPositive() // we don't want to resolve a refund
      ) {
        lastResolveAttemptTime = this._elapsedTime;
        // eslint-disable-next-line no-await-in-loop
        resolvedPayment = await this._resolvePayment(payment);
      }
      if (get(resolvePayment, 'resolvePayment.arguments.status', '') !== 'pending') {
        // eslint-disable-next-line no-await-in-loop
        paymentResponse = await this._getPaymentStatus(payment);
      }
      if (paymentResponse) return paymentResponse;
    }

    // If the timer expires, cancel the payment and have the merchant retry
    payment.cancelReason = PaymentModel.CancelReasons.TIMEOUT;
    await this.cancelPayment(payment);

    // If we reach this point, we stopped polling, throw an error
    throw new PollingTimeoutError(payment);
  }

  /**
   * This method deals with errors that are not handled by the partner service
   *
   * @param {object} error that contains unhandledErrorCode and unhandledErrorMessage
   * @param {object} terminalPayment optional
   */
  _handleServiceError(error = new Error(), terminalPayment = null) {
    if (error instanceof TerminalTransactionResponse) {
      return error;
    }

    if (error instanceof PollingTimeoutError) {
      return new TerminalTransactionResponse(
        Terminal.Response.TIMEOUT,
        Terminal.Status.ERROR,
        terminalPayment,
        Localize.translate(
          `Unable to process the payment. Terminal processing time-out occurred, you can re-attempt the payment. If the problem persists please contact support.`
        )
      );
    }

    let { code, message } = error;
    let status = Terminal.Status.ERROR;
    let response = Terminal.Response.ERROR;

    switch (code) {
      case UnhandledStripeServiceError.expired_card.code:
      case UnhandledStripeServiceError.invalid_expiry_month.code:
      case UnhandledStripeServiceError.invalid_expiry_year.code:
      case UnhandledStripeServiceError.incorrect_number.code:
        response = Terminal.Response.DECLINED;
        break;
      case UnhandledStripeServiceError.reader_error.code:
        response = Terminal.Response.TERMINAL_ERROR;
        if (message.includes('readReusableCard is currently not available')) {
          message = 'Unreferenced refund is not supported on Verifone P400 in Canada.';
        }
        if (message.includes('Action canceled by customer')) {
          response = Terminal.Response.CANCELLED_FROM_TERMINAL;
          status = Terminal.Status.DISCONNECTED;
        }
        break;
      case UnhandledStripeServiceError.payment_intent_unexpected_state.code:
        response = Terminal.Response.SERVICE_EXCEPTION;
        message = `There was a problem capturing the previous payment. You cannot attempt the same payment a second time.\n If you cannot finalize the sale please contact support.`;
        break;
      case UnhandledStripeServiceError.setup_intent_unexpected_state.code:
        response = Terminal.Response.SERVICE_EXCEPTION;
        message = `Unable to process the payment. An unknown error occurred with the terminal, you can re-attempt the payment. If the problem persists please contact support.`;
        break;
      case UnhandledStripeServiceError.canceled.code:
        response = Terminal.Response.CANCELLED_FROM_POS;
        status = Terminal.Status.DISCONNECTED;
        break;
      case UnhandledStripeServiceError.service_exception.code:
        response = Terminal.Response.SERVICE_EXCEPTION;
        break;
      case UnhandledStripeServiceError.card_declined.code:
        response = Terminal.Response.DECLINED;
        message = error.message;
        break;
    }

    // If code is unknown or not defined in the list of UnhandledStripeServiceError
    // Use Mapping based on error message.
    if (!code || !UnhandledStripeServiceError[code]) {
      if (
        message &&
        message.includes(
          'The card presented during refund is not the same card used for the original purchase.'
        )
      ) {
        response = Terminal.Response.DECLINED;
      }
    }

    return new TerminalTransactionResponse(
      response,
      status,
      terminalPayment,
      Localize.translate(
        message ||
          `Unable to process the payment. An unknown error occurred with the terminal, you can re-attempt the payment. If the problem persists please contact support.`
      )
    );
  }

  /**
   * Process a payment on the Stripe Terminal Service by doing the following:
   * 1. Collect the payment method for the specified paymentToken
   * 2. Process the payment intent returned by the collect payment method
   *
   * @param {String} paymentToken
   */
  async _processPayment(paymentToken) {
    try {
      const { paymentIntent } = await this.stripeTerminalServiceInstance.collectPaymentMethod(
        paymentToken
      );

      await this.stripeTerminalServiceInstance.processPaymentIntent(paymentIntent);

      // We set the resolve Payment Flag to true in order
      // to not restart indefinitely the _processPayment.
      this.terminalResolvedPayment = true;
    } catch (err) {
      throw err;
    }
  }

  /**
   * Process a refund on the Stripe Terminal Service by doing the following:
   * 1. Collect the refund payment method for the specified refundChargeId and amount
   * 2. Process the refund
   *
   * @param {String} refundChargeId
   * @param {Decimal} refundAmount
   * @param {Payment} payment
   */
  async _processRefund(refundChargeId, refundAmount, payment) {
    try {
      // We set the resolve Payment Flag to true in order
      // to not restart indefinitely the _processRefund.
      this.terminalResolvedPayment = true;

      await this.stripeTerminalServiceInstance.collectRefundPaymentMethod(
        refundChargeId,
        refundAmount.abs().multipliedBy(100).toNumber(),
        get(payment.get('cardPaymentRequest'), 'parameters.reverseTransfer', 'true') === 'true'
      );

      const data = await this.stripeTerminalServiceInstance.processRefund();
      const refundId = get(data, 'refund.id');

      if (
        get(this.gatewayConfig, 'attributes.parameters.platformVersion', '') ===
        GatewayConfig.PLATFORM_VERSIONS.v3
      ) {
        // The Pay Service needs the refund Id to check on the status of the
        // payment to complete it
        payment.processorRefundId = refundId;
        await payment.updatePaymentStatus();
      }
    } catch (err) {
      throw err;
    }
  }

  /**
   * Process an unreferenced refund on the Stripe Terminal Service by doing the following:
   * 1. Connect to the reader
   * 2. Collect the refund payment method
   *
   */
  async _processUnreferencedRefund(gatewayConfig, reader) {
    let paymentMethodId = '';

    // SetupIntent wont work with the p400 so we default back to previous method
    // of collecting paymentMethod via readReusableCard
    if (
      get(gatewayConfig, 'attributes.parameters.platformVersion', '') ===
        GatewayConfig.PLATFORM_VERSIONS.v3 &&
      reader.device_type !== 'verifone_P400'
    ) {
      // 1. Fetch the setup intent from payservice and enable the cancel button
      const setupIntentId = await this.stripeTerminalServiceInstance.createSetupIntent();

      if (Modules.hasModule('payments.clientSide.CancelPayment')) {
        const enableCancelButtonEvent = new CustomEvent(Terminal.TerminalEvents.PAYMENT_STARTED, {
          detail: { disableCancelButton: false },
        });
        window.dispatchEvent(enableCancelButtonEvent);
      }

      // 2. Collect Payment Method with setup intent, this will prompt the user to insert the card
      let { setupIntent } =
        await this.stripeTerminalServiceInstance.collectSetupIntentPaymentMethod(setupIntentId);

      // 3. Confirm the setup intent
      ({ setupIntent } = await this.stripeTerminalServiceInstance.confirmSetupIntent(setupIntent));

      paymentMethodId = get(
        setupIntent,
        'latest_attempt.payment_method_details.card_present.generated_card'
      );
    } else {
      const paymentMethod =
        await this.stripeTerminalServiceInstance.collectUnreferencedRefundPaymentMethod();

      paymentMethodId = get(paymentMethod, 'payment_method.id');
    }

    // We set the resolve Payment Flag to true in order
    // to not restart indefinitely the _processUnreferencedRefund.
    this.terminalResolvedPayment = true;

    return paymentMethodId;
  }

  /**
   * Helper function to retrieve the status of the payment from the API.  Once the status is no longer
   * pending the promise will be resolved or rejected accordingly.
   *
   * @param {Payment} payment     The payment whose status is currently unknown
   *                              when the status of the payment has been resolved.
   * @private
   */
  async _getPaymentStatus(payment) {
    try {
      await payment.fetch();
      this.goodPoll();
    } catch (err) {
      this.badPoll();
      return;
    }

    if (payment instanceof Payment.constructor) {
      throw new Error(
        `Unable to process the current payment. The payment you are trying to process is invalid. You might want to start a new sale or contact support.`
      );
    }
    return payment;
  }

  async _resolvePayment(payment) {
    try {
      // this is an auto resolve. It won't attempt to reprocess the payment on the terminal if server-driven
      return await resolvePayment(payment?.id, true);
    } catch (err) {
      throw new Error(`Attempting to auto resolve the payment has failed`);
    }
  }

  /**
   * Check if the current payment request is a refund
   * 1. If payment_token starts with 'ch_'
   * 2. Check if the amount is negative
   *
   * @param {String} paymentToken extracted from the Payment
   * @param {Decimal} amount extracted from the Payment
   */
  _isCardPaymentRequestRefund(paymentToken = '', amount) {
    return !!(paymentToken && paymentToken.match(/^ch_/) && amount && amount.isNegative());
  }

  /**
   * Check if the error returned is handled or not
   * by a Stripe webhook
   *
   * @param {object} unhandledError that contains a code and message associated to the error
   *
   * @returns {boolean} Return true if the error code is found in the list of UnhandledStripeServiceError
   */
  _isUnhandledStripeServiceError({ code }) {
    // We check:
    // 1. The unhandledErrorCode is falsy
    // 2. Or the unhandledErrorCode is in the list of UnhandledStripeServiceError

    return !code || !!UnhandledStripeServiceError[code];
  }

  /**
   * Check if the Stripe's payment is in a cancelable error state
   *
   * PAYMENT_INTENT_UNEXPECTED_STATE and CANCELED are the two states that have currently been identified
   * as a problematic state that would prevent a cancellation of the payment.
   *
   * CANCELED is reached when POS cancels the payment on terminal.
   * Maybe more error states could be added here in the future.
   *
   * @param {object} unhandledError that contains a code and message associated to the error
   *
   * @returns {boolean} Return true if the error code is not found in the
   * specific list of UnhandledStripeServiceErrors
   */
  _isCancelableError({ code }) {
    return ![
      UnhandledStripeServiceError.payment_intent_unexpected_state.code,
      UnhandledStripeServiceError.canceled.code,
    ].includes(code);
  }
}
